import { EntityRepository } from '@/interfaces'
import model from './model'
import type ObjectInterface from './interface'
import { hydrateEntityObject } from '@/entities/lib'
import graphQLClient from '@/lib/api/graphQLClient'
import { TFunction } from 'next-i18next'
import { useMemo } from 'react'
import useSWR from 'swr'
import travelObject from '@/entities/travel/interface'
import cmsHeaderSliderBlockSlideObject from '@/entities/cmsHeaderSliderBlockSlide/interface'

import {
  ALL_HEADER_SLIDER_BLOCKS_QUERY,
  SINGLE_HEADER_SLIDER_BLOCK_QUERY,
  CREATE_HEADER_SLIDER_BLOCK_MUTATION,
  UPDATE_HEADER_SLIDER_BLOCK_MUTATION,
  DELETE_HEADER_SLIDER_BLOCK_MUTATION,
  FORCE_DELETE_HEADER_SLIDER_BLOCK_MUTATION,
  ADD_HEADER_SLIDER_BLOCK_TRAVELS_MUTATION,
  REMOVE_HEADER_SLIDER_BLOCK_TRAVELS_MUTATION,
  CREATE_HEADER_SLIDER_BLOCK_TRAVELS_MUTATION,
  DELETE_HEADER_SLIDER_BLOCK_TRAVELS_MUTATION,
  UPDATE_HEADER_SLIDER_BLOCK_TRAVELS_ORDER_MUTATION,
  ADD_HEADER_SLIDER_BLOCK_SLIDES_MUTATION,
  REMOVE_HEADER_SLIDER_BLOCK_SLIDES_MUTATION,
  CREATE_HEADER_SLIDER_BLOCK_SLIDES_MUTATION,
  DELETE_HEADER_SLIDER_BLOCK_SLIDES_MUTATION,
  UPDATE_HEADER_SLIDER_BLOCK_SLIDES_ORDER_MUTATION,
} from './graphql'

const hydrateObjects = (objects: Array<any>) => {
  return objects.map((object) => hydrateEntityObject(model, object))
}

const hydrateObject = (object: any) => {
  return hydrateEntityObject(model, object)
}
const cmsHeaderSliderBlockRepository: EntityRepository = {
  getObject: async (id: string | number, includes = {}): Promise<any> => {
    const variables = { id: id, ...includes }
    return graphQLClient.request(SINGLE_HEADER_SLIDER_BLOCK_QUERY, variables).then((data: any) => {
      return hydrateObject(data.headerSliderBlock)
    })
  },

  getObjects: async (includes = {}): Promise<any> => {
    return graphQLClient.request(ALL_HEADER_SLIDER_BLOCKS_QUERY, includes).then((data: any) => {
      return hydrateObjects(data.headerSliderBlocks)
    })
  },

  useObject: (id: string | number, includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includes }), [id])
    const { data, error, mutate } = useSWR(paused ? null : [SINGLE_HEADER_SLIDER_BLOCK_QUERY, variables], { revalidateOnFocus: false })
    return {
      object: !data ? data : hydrateObject(data.headerSliderBlock),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  useObjects: (includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => includes, [])
    const { data, error, mutate } = useSWR(paused ? null : [ALL_HEADER_SLIDER_BLOCKS_QUERY, variables])
    return {
      objects: !data ? data : hydrateObjects(data.headerSliderBlocks),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  createObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(CREATE_HEADER_SLIDER_BLOCK_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.headerSliderBlock)
    })
  },

  updateObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(UPDATE_HEADER_SLIDER_BLOCK_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.headerSliderBlock)
    })
  },

  deleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(DELETE_HEADER_SLIDER_BLOCK_MUTATION, variables).then((data: any) => {
      return data.deleteCmsHeaderSliderBlock
    })
  },

  forceDeleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(FORCE_DELETE_HEADER_SLIDER_BLOCK_MUTATION, variables).then((data: any) => {
      return data.forceDeleteCmsHeaderSliderBlock
    })
  },

  assignTravels: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        travelIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_HEADER_SLIDER_BLOCK_TRAVELS_MUTATION, variables)
  },

  unassignTravels: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        travelIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_HEADER_SLIDER_BLOCK_TRAVELS_MUTATION, variables)
  },

  createTravels: async (id: string, items: Array<travelObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        travels: items,
      },
    }
    return graphQLClient.request(CREATE_HEADER_SLIDER_BLOCK_TRAVELS_MUTATION, variables)
  },

  deleteTravels: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        travelIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_HEADER_SLIDER_BLOCK_TRAVELS_MUTATION, variables)
  },

  updateTravelsOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        travelIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_HEADER_SLIDER_BLOCK_TRAVELS_ORDER_MUTATION, variables)
  },

  assignSlides: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        slideIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_HEADER_SLIDER_BLOCK_SLIDES_MUTATION, variables)
  },

  unassignSlides: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        slideIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_HEADER_SLIDER_BLOCK_SLIDES_MUTATION, variables)
  },

  createSlides: async (id: string, items: Array<cmsHeaderSliderBlockSlideObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        slides: items,
      },
    }
    return graphQLClient.request(CREATE_HEADER_SLIDER_BLOCK_SLIDES_MUTATION, variables)
  },

  deleteSlides: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        slideIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_HEADER_SLIDER_BLOCK_SLIDES_MUTATION, variables)
  },

  updateSlidesOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        slideIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_HEADER_SLIDER_BLOCK_SLIDES_ORDER_MUTATION, variables)
  },
}

export default cmsHeaderSliderBlockRepository
